import { type ReactElement, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button, Dialog, toast } from "@olivahealth/oli-ui";
import type { DisconnectOrgSlackMutation } from "./__generated__/DisconnectOrgSlackMutation.graphql";
import OrganisationSelector from "./OrganisationSelector";

const DisconnectOrgSlackMutation = graphql`
  mutation DisconnectOrgSlackMutation($organisationId: ID!) {
    disconnectOrganisationFromSlack(organisationId: $organisationId) {
      __typename
      ... on OrganisationDisconnectedFromSlack {
        success
        organisationId
      }
      ... on ForbiddenError {
        reason
      }
      ... on NotFoundError {
        reason
      }
    }
  }
`;

export default function DisconnectOrgSlack(): ReactElement {
  const [selectedOrganisationId, setSelectedOrganisationId] =
    useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mutate, isDisconnecting] = useMutation<DisconnectOrgSlackMutation>(
    DisconnectOrgSlackMutation,
  );

  const handleDisconnectOrganisation = () => {
    if (!selectedOrganisationId) return;

    mutate({
      variables: {
        organisationId: selectedOrganisationId,
      },
      onCompleted: (response) => {
        setIsDialogOpen(false);
        if (
          response.disconnectOrganisationFromSlack.__typename ===
          "OrganisationDisconnectedFromSlack"
        ) {
          toast({
            body: "Organisation has been disconnected from Slack successfully",
            variant: "success",
          });
          setSelectedOrganisationId("");
        } else if (
          response.disconnectOrganisationFromSlack.__typename ===
          "ForbiddenError"
        ) {
          toast({
            body: response.disconnectOrganisationFromSlack.reason,
            variant: "error",
          });
        } else if (
          response.disconnectOrganisationFromSlack.__typename ===
          "NotFoundError"
        ) {
          toast({
            body: response.disconnectOrganisationFromSlack.reason,
            variant: "error",
          });
        }
      },
      onError: (error) => {
        setIsDialogOpen(false);
        toast({ body: error.message, variant: "error" });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">
        Disconnect Organisation from Slack
      </h3>
      <div className="flex gap-4">
        <div className="flex-1">
          <OrganisationSelector
            onOrganisationSelected={(org) => setSelectedOrganisationId(org.id)}
            placeholder="Select an organisation to disconnect..."
          />
        </div>
        <Button
          disabled={!selectedOrganisationId || isDisconnecting}
          onClick={() => setIsDialogOpen(true)}
          variant="danger"
        >
          Disconnect from Slack
        </Button>
      </div>
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        variant="centered"
        size="sm"
        showCloseButton={false}
      >
        <div className="flex flex-col items-center gap-y-4 text-center">
          <h3 className="text-lg font-semibold">
            Disconnect Organisation from Slack
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This will disconnect all users in this organisation from Slack.
            Their Slack tokens and user IDs will be removed.
          </p>
          <div className="flex gap-4">
            <Button
              variant="danger"
              onClick={handleDisconnectOrganisation}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
              disabled={isDisconnecting}
              className="dark:text-white dark:border-white"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
